import React, { useState } from 'react';
import translate from '@data/translations';
import { Reservation, ReservationController } from '../resController';
import { CustomersAndCompanies } from './CustomersAndCompanies';
import { CompanyModel, CustomerModel, ReservationModel, ReservationStatus, UserInfo } from '@common/modelDefinition';
import ReservationDateTimeSelector from './ReservationDateTimeSelector';
import AccommodationPriceOffer from './accommodationPriceOffer';
import { setModalContent } from '@components/modal';
import Delete from '@components/confirmModal';
import StatusButtons from './statusButtons';
import OpenPrice from './OpenPrice';
import dayjs from 'dayjs';
import useStore from '@data/state/zustand';
import { toast } from 'react-toastify';
import { InvoicedStatusEnum } from '@common/enums';

interface MainReservationInfoProps {
    history: any;
    otherSettings: any;
}

function MainReservationInfo(props: MainReservationInfoProps) {
    const { history, otherSettings } = props;
    const ctrl = new ReservationController();

    const loggedUser: UserInfo = useStore((appState: any) => appState.authTokenInfo?.user);
    const isPending: boolean = useStore((appState: any) => appState.state.reservationForm2.isPending);

    const reservation = ctrl.getActiveReservation();

    const [reservationNotes, setReservationNotes] = useState<string>(reservation?.notes || '');

    React.useEffect(() => {
        setReservationNotes(reservation?.notes || '');
    }, [reservation?.notes]);

    const onReservationInputChange = (e: any) => {
        const res = { ...reservation };
        if (e.target.name === 'statusEnum') {
            (res as any)[e.target.name] = Number(e.target.value);
            if (Number(e.target.value) === 2) {
                (res as any)['checkInTimestamp'] = new Date().getTime();
            }
        } else {
            (res as any)[e.target.name] = e.target.value;
        }
        ctrl.updateActiveReservation(res as ReservationModel);
    };

    const editCustomer = (updatedCustomer: CustomerModel) => {
        try {
            if (reservation?.statusEnum !== ReservationStatus.closed) {
                ctrl.setReservationHolder({ customer: updatedCustomer, company: reservation.Company });
                if (updatedCustomer?.id && reservation.customerId !== updatedCustomer.id) {
                    const guestInVisaInfo =
                        reservation.Customer &&
                        reservation?.VisaInfo?.find((c) => c.customerId === reservation.Customer?.id);
                    if (guestInVisaInfo && guestInVisaInfo.id) {
                        ctrl.removeGuestFromReservation(guestInVisaInfo.id);
                    }
                }
            } else {
                toast(translate('Reservation closed!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.ERROR,
                });
            }
        } catch (error: any) {}
    };

    const editCompany = (updatedCompany: CompanyModel) => {
        ctrl.setReservationHolder({ customer: reservation.Customer, company: updatedCompany });
    };

    const showDeleteModal = (cusOrComp: string) => {
        setModalContent(
            <Delete
                actionForYes={() => {
                    try {
                        if (reservation?.statusEnum !== ReservationStatus.closed) {
                            if (cusOrComp === 'customer') {
                                ctrl.setReservationHolder({ customer: null, company: reservation.Company });
                                const guestInVisaInfo =
                                    reservation.Customer &&
                                    reservation?.VisaInfo?.find((c) => c.customerId === reservation.Customer?.id);
                                if (guestInVisaInfo && guestInVisaInfo.id) {
                                    ctrl.removeGuestFromReservation(guestInVisaInfo.id);
                                }
                            } else {
                                ctrl.setReservationHolder({ customer: reservation.Customer, company: null });
                            }
                        } else {
                            toast(translate('Reservation closed!'), {
                                position: toast.POSITION.TOP_RIGHT,
                                type: toast.TYPE.ERROR,
                            });
                        }
                    } catch (error: any) {}
                }}
            />,
            translate('Delete') + ' ' + translate('reservation holder'),
            false,
            'modal-md'
        );
    };

    const isCheckedInOnLoad = reservation?.statusEnum === ReservationStatus.checkedIn;
    const isAutoBlocked = reservation?.statusEnum === ReservationStatus.autoBlocked;

    const isAdmin = loggedUser
        ? ['superAdmin', 'admin', 'managerFull', 'managerBussiness'].includes(loggedUser.role)
        : false;

    return (
        <div className="">
            <div className="display-flex mt-4p mobile-page-form">
                <div className="flex">
                    <label className="text-bold ml-4p mb-8p">{translate('Holder name')}</label>
                    <CustomersAndCompanies
                        company={reservation.Company}
                        customer={reservation.Customer}
                        history={history}
                        reservation={reservation}
                        customerSelect={async (c: CustomerModel) => {
    try {
        const res = { ...reservation };
        res.Customer = c;
        res.customerId = c.id;

        // Ako NOVA rezervacija (nema id), samo postavi holdera
        if (!res.id) {
            // res.adultsNumber = 1;
            ctrl.updateActiveReservation(res as ReservationModel);
            return;
        }

        // Ako rezervacija postoji, provjeri da li je holder već dodan kao gost
        const alreadyAdded = reservation.VisaInfo?.find((v) => v.customerId === c.id);

        if (alreadyAdded) {
            // Holder je već gost, samo ažuriraj holdera
            ctrl.updateActiveReservation(res as ReservationModel);
            return;
        }

        // Holder NIJE gost - dodaj ga i linkaj na slot s isHolder: true ili prvi prazan

        // 1. Parsiraj guestTaxConfig
        const currentConfig = res.guestTaxConfig
            ? typeof res.guestTaxConfig === 'string'
                ? JSON.parse(res.guestTaxConfig)
                : res.guestTaxConfig
            : { adults: [], children: [] };

        // 2. Nađi slot s isHolder: true i visaInfoId === null (prazan holder slot)
        let emptySlotIndex = (currentConfig.adults || []).findIndex(
            (a: any) => a.isHolder === true && a.visaInfoId === null
        );
        
        // Ako nema takvog, nađi prvi prazan slot
        if (emptySlotIndex === -1) {
            emptySlotIndex = (currentConfig.adults || []).findIndex(
                (a: any) => a.visaInfoId === null
            );
        }

        // 3. Dodaj holdera kao gosta
        await ctrl.addGuestToReservation(c, null, null, 'tax');

        // 4. Dohvati ažuriranu rezervaciju
        const updatedRes = ctrl.getActiveReservation();
        const newVisaInfo = updatedRes.VisaInfo?.find((v) => v.customerId === c.id);

        if (!newVisaInfo?.id) {
            // Nešto nije OK, ali holder je dodan
            ctrl.updateActiveReservation(res as ReservationModel);
            return;
        }

        // 5. Pripremi guestTaxConfig
        const finalConfig = updatedRes.guestTaxConfig
            ? typeof updatedRes.guestTaxConfig === 'string'
                ? JSON.parse(updatedRes.guestTaxConfig)
                : JSON.parse(JSON.stringify(updatedRes.guestTaxConfig))
            : { adults: [], children: [] };

        // 6. Ako je bio stari holder slot, makni mu isHolder flag
        finalConfig.adults.forEach((a: any) => {
            if (a.isHolder) {
                a.isHolder = false;
            }
        });

        const targetIndex = emptySlotIndex !== -1 ? emptySlotIndex : finalConfig.adults.length;

        // Osiguraj da slot postoji
        while (finalConfig.adults.length <= targetIndex) {
            finalConfig.adults.push({
                visaInfoId: null,
                fixedTaxId: null,
                fixedTaxId2: null,
                fixedTaxType: 'tax',
                checkInTimestamp: updatedRes.checkInTimestamp,
                checkOutTimestamp: updatedRes.checkOutTimestamp,
                isHolder: false,
            });
        }

        // Zapamti postojeće datume slota PRIJE linkanja
        const slotCheckIn = finalConfig.adults[targetIndex]?.checkInTimestamp || updatedRes.checkInTimestamp;
        const slotCheckOut = finalConfig.adults[targetIndex]?.checkOutTimestamp || updatedRes.checkOutTimestamp;

        // Linkaj gosta na slot + postavi isHolder: true
        finalConfig.adults[targetIndex] = {
            ...finalConfig.adults[targetIndex],
            visaInfoId: newVisaInfo.id,
            isHolder: true,
        };

        // 7. Ažuriraj rezervaciju
        const finalRes = { ...updatedRes };
        finalRes.Customer = c;
        finalRes.customerId = c.id;
        finalRes.guestTaxConfig = finalConfig;

        // 8. Ako slot ima custom datume, ažuriraj i VisaInfo
        const hasCustomDates =
            slotCheckIn !== updatedRes.checkInTimestamp ||
            slotCheckOut !== updatedRes.checkOutTimestamp;

        if (hasCustomDates) {
            const editVisaData = { ...newVisaInfo };
            editVisaData.checkInTimestamp = slotCheckIn;
            editVisaData.checkOutTimestamp = slotCheckOut;
            await ctrl.updateReservationGuestReference(editVisaData);

            // Ažuriraj lokalni VisaInfo
            if (finalRes.VisaInfo) {
                finalRes.VisaInfo = finalRes.VisaInfo.map((v) => {
                    if (v.id === newVisaInfo.id) {
                        return { ...v, checkInTimestamp: slotCheckIn, checkOutTimestamp: slotCheckOut };
                    }
                    return v;
                });
            }
        }

        ctrl.updateActiveReservation(finalRes as ReservationModel);
    } catch (error: any) {
        toast(translate(error.message), { type: toast.TYPE.ERROR });
    }
}}
                        companySelect={(c: CompanyModel) => {
                            const res = { ...reservation };
                            res.Company = c;
                            res.companyId = c.id;
                            try {
                                ctrl.updateActiveReservation(res as ReservationModel);
                            } catch (error: any) {
                                toast(translate(error.message), { type: toast.TYPE.ERROR });
                            }
                        }}
                        editCustomer={editCustomer}
                        deleteCustomer={() => showDeleteModal('customer')}
                        editCompany={editCompany}
                        deleteCompany={() => showDeleteModal('company')}
                        flexColumn={true}
                        width="400px"
                        customerCompanyStyle={{
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            borderRadius: '6px',
                            backgroundColor: '#fbfbfb',
                            padding: '5px',
                        }}
                    />
                </div>
                <div className="flex flex-column flex-center-center mobile-mt-12p">
                    {reservation?.id ? (
                        <StatusButtons
                            statusButtonActionHandler={(action: string, applyCurrentTime?: boolean) => {
                                const res = { ...reservation };
                                if (action === 'confirm') {
                                    res.statusEnum = ReservationStatus.confirmed;
                                    res.canceledReservationInvoicingStatus = 0;
                                } else if (action === 'checkin') {
                                    res.statusEnum = ReservationStatus.checkedIn;
                                    if (applyCurrentTime) {
                                        res.checkInTimestamp = Date.now();
                                    }
                                } else if (action === 'cancel') {
                                    res.statusEnum = ReservationStatus.canceled;
                                    if (reservation.previousGroupUuid) {
                                        reservation.previousGroupUuid = null; //Ako je izbačena iz grupe, pa nakon tog i otkazana, izbaci je s računa
                                    }
                                } else if (action === 'checkout') {
                                    //checkout only throught checkout form (payment)
                                }
                                try {
                                    ctrl.updateActiveReservation(res as ReservationModel);
                                } catch (error: any) {
                                    toast(translate(error.message), { type: toast.TYPE.ERROR });
                                }
                            }}
                            history={history}
                            reservation={reservation}
                            isPending={isPending}
                        />
                    ) : (
                        <div className="ml-12p">
                            <label className="text-bold ml-4p mb-4p">{translate('Reservation status')}</label>
                            <div className="p-1">
                                <select
                                    onChange={onReservationInputChange}
                                    name="statusEnum"
                                    value={reservation.statusEnum}
                                    className="input input-modal"
                                    style={{ width: '180px' }}
                                >
                                    <option disabled={isAutoBlocked || isCheckedInOnLoad} value={0}>
                                        {translate('Early bird')}
                                    </option>
                                    <option disabled={isCheckedInOnLoad} value={1}>
                                        {translate('Confirmed')}
                                    </option>
                                    <option
                                        disabled={
                                            reservation?.id ? (reservation?.statusEnum === 4 ? true : false) : true
                                        }
                                        value={2}
                                    >
                                        {translate('Checked in')}
                                    </option>
                                    <option
                                        disabled={
                                            (reservation?.id ? (reservation?.statusEnum === 2 ? true : false) : true) &&
                                            !isAdmin
                                        }
                                        value={3}
                                    >
                                        {translate('Canceled')}
                                    </option>
                                    <option disabled={reservation.statusEnum !== ReservationStatus.closed} value={4}>
                                        {translate('Close')}
                                    </option>
                                    <option disabled={true} value={5}>
                                        {translate('Auto blocked')}
                                    </option>
                                </select>
                            </div>
                        </div>
                    )}
                    {reservation.statusEnum === ReservationStatus.canceled ? (
                        <div className="mt-8p ml-12p">
                            <label className="text-bold ml-4p mt-8p mb-4p">{translate('Invoicing method')}</label>
                            <div className="p-1">
                                <select
                                    onChange={async (e) => {
                                        const res = { ...reservation };
                                        res.canceledReservationInvoicingStatus = Number(e.target.value);
                                        if (Number(e.target.value) !== 0) {
                                            res.accommodationPriceListId = null;
                                            res.openPrice = null;
                                        }
                                        try {
                                            ctrl.updateActiveReservation(res as ReservationModel);
                                        } catch (error: any) {
                                            toast(translate(error.message), { type: toast.TYPE.ERROR });
                                        }
                                    }}
                                    name="canceledReservationInvoicingStatus"
                                    value={reservation?.canceledReservationInvoicingStatus}
                                    className="input input-modal"
                                    style={{ width: '180px' }}
                                >
                                    <React.Fragment>
                                        {['Full price', 'Partial fee', 'No charge'].map(
                                            (option: string, key: number) => {
                                                return (
                                                    <option value={key} key={key}>
                                                        {translate(option)}
                                                    </option>
                                                );
                                            }
                                        )}
                                    </React.Fragment>
                                </select>
                            </div>
                        </div>
                    ) : reservation.id ? (
                        <div className="mt-8p ml-12p">
                            <label className="text-bold ml-4p mt-8p mb-4p">{translate('Payment status')}</label>
                            <div
                                className="text-bold"
                                style={{
                                    width: '180px',
                                    padding: '8px',
                                    border: '1px solid',
                                    borderRadius: '4px',
                                    borderColor:
                                        reservation?.invoicedStatus === InvoicedStatusEnum['fully paid']
                                            ? '#28a745'
                                            : reservation?.invoicedStatus === InvoicedStatusEnum['Partial payment']
                                            ? '#ffc107'
                                            : reservation?.invoicedStatus === InvoicedStatusEnum.notPaid
                                            ? '#f3b2b9'
                                            : '#6c757d',
                                    backgroundColor:
                                        reservation?.invoicedStatus === InvoicedStatusEnum['fully paid']
                                            ? '#d4edda'
                                            : reservation?.invoicedStatus === InvoicedStatusEnum['Partial payment']
                                            ? '#fff3cd'
                                            : reservation?.invoicedStatus === InvoicedStatusEnum.notPaid
                                            ? '#fff2f2'
                                            : '#f8f9fa',
                                    color:
                                        reservation?.invoicedStatus === InvoicedStatusEnum['fully paid']
                                            ? '#155724'
                                            : reservation?.invoicedStatus === InvoicedStatusEnum['Partial payment']
                                            ? '#856404'
                                            : reservation?.invoicedStatus === InvoicedStatusEnum.notPaid
                                            ? '#721c24'
                                            : '#6c757d',
                                }}
                            >
                                <div className="text-bold" style={{ width: '180px' }}>
                                    {reservation?.invoicedStatus === InvoicedStatusEnum['fully paid'] && (
                                        <span>{translate('Fully paid')}</span>
                                    )}
                                    {reservation?.invoicedStatus === InvoicedStatusEnum['Partial payment'] && (
                                        <span>{translate('Paid:Partialy')}</span>
                                    )}
                                    {reservation?.invoicedStatus === InvoicedStatusEnum.notPaid && (
                                        <span>{translate('Not paid')}</span>
                                    )}
                                    {reservation?.invoicedStatus === undefined ||
                                    reservation?.invoicedStatus === null ? (
                                        <span className="text-muted">{translate('Not set')}</span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {reservation.canceledReservationInvoicingStatus === 1 ? (
                        <div className="mt-4p justify-content-center align-items-center d-inline">
                            <label className="text-bold mt-4p mb-4p">
                                {translate('The cost of canceling a reservation')}
                            </label>
                            <OpenPrice reservation={reservation} />
                        </div>
                    ) : null}
                </div>
            </div>
            <hr />
            <div className="mt-8p mb-8p">
                <ReservationDateTimeSelector
                    reservation={reservation}
                    onReservationUpdate={(reservation: Reservation) => {
                        const res = { ...reservation };
                        ctrl.updateActiveReservation(res);
                    }}
                />
            </div>
            <AccommodationPriceOffer history={history} room={reservation.RoomInfo} reservation={reservation} />
            <hr />
            <div className="">
                {reservation?.id ? (
                    <>
                        <div
                            className=""
                            style={{
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                borderRadius: '6px',
                                backgroundColor: '#fbfbfb',
                                padding: '5px',
                            }}
                        >
                            <ReservationMadeBy reservation={reservation} otherSettings={otherSettings} />
                        </div>
                        <br />
                    </>
                ) : null}
                <label className="text-bold ml-4p mb-4p">{translate('Reservation notes')}</label>
                <textarea
                    rows={1}
                    className="input input-modal mt-4p"
                    placeholder={translate('Reservation notes')}
                    name="notes"
                    value={reservationNotes}
                    onChange={(e) => {
                        setReservationNotes(e.target.value);
                    }}
                    onBlur={() => {
                        if (reservationNotes !== (reservation?.notes || '')) {
                            const res = { ...reservation };
                            res.notes = reservationNotes;
                            try {
                                ctrl.updateActiveReservation(res as ReservationModel);
                            } catch (error: any) {
                                toast(translate(error.message), { type: toast.TYPE.ERROR });
                                setReservationNotes(reservation?.notes || '');
                            }
                        }
                    }}
                ></textarea>
            </div>
        </div>
    );
}

function ReservationMadeBy(props: { reservation: Reservation; otherSettings: any }) {
    const { reservation, otherSettings } = props;
    const user = reservation.Recepcionist;
    const editedByUser = reservation.EditedByUser;

    const userNotExists = user === null || user === undefined;
    const isChannelManager = userNotExists && reservation.providerReservation;

    if (userNotExists && !isChannelManager) {
        return null;
    }

    let reservationMadeByName;
    if (isChannelManager) {
        reservationMadeByName = 'Channel Manager';
    } else {
        reservationMadeByName = user!.name ? `${user!.name} ${user!.surname} ${user!.email}` : `${user!.email}`;
    }

    let reservationEditedByName;
    if (editedByUser) {
        reservationEditedByName = editedByUser!.name
            ? `${editedByUser!.name} ${editedByUser!.surname} ${editedByUser!.email}`
            : `${editedByUser!.email}`;
    }

    return (
        <div className="display-flex flex-column">
            <div className="flex-start-between">
                <div className="w-60">
                    <b>
                        {translate('Created by')}
                        {': '}
                    </b>
                    {reservationMadeByName}
                </div>
                <div className="w-40 text-right mr-4p">
                    <b>
                        {translate('Res created at')}
                        {': '}
                    </b>
                    {dayjs((reservation as any).createdAt).format(
                        otherSettings?.dateFormat ? otherSettings.dateFormat + ' HH:mm' : 'DD/MM/YYYY HH:mm'
                    )}
                </div>
            </div>
            <div className="flex-start-between mt-4p">
                {reservationEditedByName ? (
                    <div className="w-60">
                        <b>
                            {translate('Updated by')}
                            {': '}
                        </b>
                        {reservationEditedByName}
                    </div>
                ) : null}
                {(reservation as any).createdAt !== (reservation as any).updatedAt && (
                    <div className="w-40 text-right mr-4p">
                        <b>
                            {translate('Res updated at')}
                            {': '}
                        </b>
                        {dayjs((reservation as any).updatedAt).format(
                            otherSettings?.dateFormat ? otherSettings.dateFormat + ' HH:mm' : 'DD/MM/YYYY HH:mm'
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MainReservationInfo;
